import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '../ui/button';
import { ArrowRight } from 'lucide-react';

// Helper: smooth-scroll to element by id with header offset
function scrollToId(id) {
  const el = document.getElementById(id);
  if (!el) return false;
  const header = document.querySelector('header');
  const headerH = header ? header.offsetHeight : 0;
  const top = el.getBoundingClientRect().top + window.scrollY - headerH - 12;
  window.scrollTo({ top, behavior: 'smooth' });
  return true;
}

export default function PageHero({ eyebrow, title, subtitle, ctaLabel = 'Get Started', ctaTo = '/loan', badges = [] }) {
  const location = useLocation();

  // Parse ctaTo — supports: '#id', '/path#id', '/path', absolute URLs
  let hashId = '';
  let pathPart = ctaTo || '';
  if (typeof ctaTo === 'string' && ctaTo.includes('#')) {
    const [p, h] = ctaTo.split('#');
    pathPart = p;
    hashId = h;
  }
  const isSamePageHash = hashId && (pathPart === '' || pathPart === location.pathname);

  const handleClick = (e) => {
    if (!isSamePageHash) return; // let Link handle navigation
    e.preventDefault();
    // scroll now, retry after paint if element not ready
    if (!scrollToId(hashId)) {
      requestAnimationFrame(() => scrollToId(hashId));
    }
  };

  const ButtonEl = (
    <Button className="bg-emerald-600 hover:bg-emerald-700 text-white h-12 px-7 text-base shine group">
      {ctaLabel}
      <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
    </Button>
  );

  return (
    <section className="relative overflow-hidden bg-hero-glow">
      <div className="absolute inset-0 grid-pattern opacity-50" />
      <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-10 pb-14 sm:pt-16 sm:pb-20 lg:pt-24 lg:pb-24 text-center">
        {eyebrow && (
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-emerald-200 shadow-sm text-xs font-semibold text-emerald-700 uppercase tracking-wider fadeInUp">
            {eyebrow}
          </div>
        )}
        <h1 className="font-serif-display mt-5 sm:mt-6 text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-slate-900 font-bold max-w-4xl mx-auto leading-[1.1] fadeInUp">
          {title}
        </h1>
        {subtitle && <p className="mt-4 sm:mt-5 text-base sm:text-lg text-slate-600 max-w-2xl mx-auto fadeInUp" style={{ animationDelay: '0.1s' }}>{subtitle}</p>}
        {badges.length > 0 && (
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            {badges.map((b) => (
              <div key={b.title} className="px-4 py-2 rounded-full bg-white border border-slate-200 text-sm text-slate-700 shadow-sm">
                <span className="font-semibold text-emerald-700">{b.title}</span> – {b.desc}
              </div>
            ))}
          </div>
        )}
        {ctaTo && (
          <div className="mt-8 fadeInUp" style={{ animationDelay: '0.2s' }}>
            {isSamePageHash ? (
              <a href={`#${hashId}`} onClick={handleClick}>{ButtonEl}</a>
            ) : (
              <Link to={ctaTo}>{ButtonEl}</Link>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
